'use client'

/**
 * PaginatedDataTable Component
 * DataTable wrapper with integrated pagination
 */

import * as React from 'react'
import { cn } from '../../lib/utils'
import { DataTable, type DataTableProps } from './data-table'
import {
  PaginationControls,
  type PaginationControlsProps,
  type PaginationControlsLabels,
} from './pagination-controls'

export interface PaginatedDataTableProps<T>
  extends DataTableProps<T>,
    Omit<PaginationControlsProps, 'className'> {
  /** Translation labels for pagination */
  labels?: PaginationControlsLabels
  /** Position of pagination controls */
  paginationPosition?: 'top' | 'bottom' | 'both'
  /** Additional className for pagination */
  paginationClassName?: string
}

export function PaginatedDataTable<T>({
  // DataTable props
  data,
  columns,
  actions,
  getRowKey,
  onRowClick,
  onRowDoubleClick,
  selectedRowKey,
  emptyState,
  isRTL = false,
  className,
  // Sort props
  sortField,
  sortOrder,
  onSort,
  // Pagination props
  page,
  pageSize,
  totalItems,
  totalPages,
  pageSizes,
  onPageChange,
  onPageSizeChange,
  showPageSizeSelector = true,
  showItemCount = true,
  paginationPosition = 'bottom',
  paginationClassName,
  labels,
}: PaginatedDataTableProps<T>) {
  const paginationElement = (
    <PaginationControls
      page={page}
      pageSize={pageSize}
      totalItems={totalItems}
      totalPages={totalPages}
      pageSizes={pageSizes}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      showPageSizeSelector={showPageSizeSelector}
      showItemCount={showItemCount}
      isRTL={isRTL}
      className={paginationClassName}
      labels={labels}
    />
  )

  return (
    <div className={cn('space-y-4', className)}>
      {(paginationPosition === 'top' || paginationPosition === 'both') &&
        paginationElement}

      <DataTable
        data={data}
        columns={columns}
        actions={actions}
        getRowKey={getRowKey}
        onRowClick={onRowClick}
        onRowDoubleClick={onRowDoubleClick}
        selectedRowKey={selectedRowKey}
        emptyState={emptyState}
        isRTL={isRTL}
        sortField={sortField}
        sortOrder={sortOrder}
        onSort={onSort}
      />

      {(paginationPosition === 'bottom' || paginationPosition === 'both') &&
        paginationElement}
    </div>
  )
}
